import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface QualityRule {
  id: string
  name: string
  description: string
  type: string
  target_table: string
  target_column: string | null
  condition: string
  severity: string
  enabled: boolean
  last_run: string | null
  last_result: { passed: boolean; errors: number; checked: number } | null
}

export default function DataQualityPage() {
  const [rules, setRules] = useState<QualityRule[]>([])
  const [statistics, setStatistics] = useState<{total_rules: number; passing: number; failing: number; overall_score: number} | null>(null)
  const [selectedRule, setSelectedRule] = useState<QualityRule | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [rulesRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/data-quality/rules'),
      fetch(API_BASE + '/data-quality/statistics')
    ])
    if (rulesRes.ok) setRules((await rulesRes.json()).rules || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const runRule = async (id: string) => {
    const res = await fetch(API_BASE + '/data-quality/rules/' + id + '/run', { method: 'POST' })
    if (res.ok) fetchData()
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'completeness': return '📋'
      case 'uniqueness': return '🔑'
      case 'validity': return '✅'
      case 'consistency': return '🔄'
      case 'timeliness': return '⏰'
      case 'accuracy': return '🎯'
      default: return '📊'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Data Quality Rules Engine</h1>

        {statistics && (
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Overall Score</div>
              <div className={`text-3xl font-bold ${
                statistics.overall_score >= 90 ? 'text-green-600' :
                statistics.overall_score >= 70 ? 'text-yellow-600' : 'text-red-600'
              }`}>{statistics.overall_score}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Rules</div>
              <div className="text-3xl font-bold">{statistics.total_rules}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Passing</div>
              <div className="text-3xl font-bold text-green-600">{statistics.passing}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Failing</div>
              <div className="text-3xl font-bold text-red-600">{statistics.failing}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Quality Rules</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                + Add Rule
              </button>
            </div>
            <div className="divide-y">
              {rules.map(rule => (
                <div
                  key={rule.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedRule?.id === rule.id ? 'bg-blue-50' : ''}`}
                  onClick={() => setSelectedRule(rule)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getTypeIcon(rule.type)}</span>
                      <div>
                        <div className="font-medium">{rule.name}</div>
                        <div className="text-sm text-gray-500">{rule.target_table}{rule.target_column ? `.${rule.target_column}` : ''}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${getSeverityColor(rule.severity)}`}>
                        {rule.severity}
                      </span>
                      {rule.last_result && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          rule.last_result.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {rule.last_result.passed ? 'Passing' : `${rule.last_result.errors} errors`}
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); runRule(rule.id); }}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Run
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            {selectedRule ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedRule.name}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500">Type</div>
                    <div className="font-medium capitalize">{selectedRule.type}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Target</div>
                    <div className="font-medium">{selectedRule.target_table}{selectedRule.target_column ? `.${selectedRule.target_column}` : ''}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Condition</div>
                    <code className="block mt-1 p-2 bg-gray-100 rounded text-sm">{selectedRule.condition}</code>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Description</div>
                    <div className="text-sm">{selectedRule.description}</div>
                  </div>
                  {selectedRule.last_run && (
                    <div>
                      <div className="text-sm text-gray-500">Last Run</div>
                      <div className="text-sm">{new Date(selectedRule.last_run).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Rule Types</h3>
                <div className="space-y-3">
                  {['completeness', 'uniqueness', 'validity', 'consistency', 'timeliness', 'accuracy'].map(type => (
                    <div key={type} className="flex items-center gap-2">
                      <span>{getTypeIcon(type)}</span>
                      <span className="capitalize">{type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
